import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid";

/**
 * Mouse-driven column resizing for the community (MIT) DataGrid v6, which
 * force-disables its built-in `resizable` option.
 *
 * Re-uses the header separator the grid already renders: drag it to resize,
 * double-click it to reset that column. Widths are persisted per table in
 * localStorage under `colWidths:<storageKey>`.
 *
 * Usage:
 *   const resizable = Index.useResizableColumns(columns, "report");
 *   <Index.Box {...resizable.containerProps}>
 *     <Index.DataGrid columns={resizable.columns} apiRef={resizable.apiRef} ... />
 *   </Index.Box>
 *
 * If the grid already has an apiRef, pass it in and keep using it:
 *   Index.useResizableColumns(columns, "key", { apiRef })
 * If the wrapper already has a className, merge it:
 *   className={`my-class ${resizable.containerProps.className}`}
 *
 * While dragging, width is pushed straight into the grid via apiRef so the
 * host page (live-tick heavy) does not re-render on every mouse move; React
 * state is only committed on mouse-up.
 */
const SEPARATOR = ".MuiDataGrid-columnSeparator";
const HEADER = ".MuiDataGrid-columnHeader";
const DEFAULT_MIN_WIDTH = 50;

const storageName = (key) => `colWidths:${key}`;

const readWidths = (key) => {
  if (!key) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(storageName(key)) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeWidths = (key, widths) => {
  if (!key) return;
  try {
    if (Object.keys(widths).length) {
      localStorage.setItem(storageName(key), JSON.stringify(widths));
    } else {
      localStorage.removeItem(storageName(key));
    }
  } catch (e) {
    // storage unavailable (private mode / quota) — resizing still works for the session
  }
};

const getFieldFromSeparator = (target) => {
  const separator = target?.closest?.(SEPARATOR);
  if (!separator) return null;
  const header = separator.closest(HEADER);
  const field = header?.getAttribute("data-field");
  return field ? { field, header } : null;
};

const useResizableColumns = (columns, storageKey, options = {}) => {
  const ownApiRef = useGridApiRef();
  const apiRef = options.apiRef || ownApiRef;
  const [widths, setWidths] = useState(() => readWidths(storageKey));
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);

  // Reload if the same component switches to a different table key.
  useEffect(() => {
    setWidths(readWidths(storageKey));
  }, [storageKey]);

  const minWidthOf = useCallback(
    (field) =>
      columns?.find((col) => col.field === field)?.minWidth ??
      DEFAULT_MIN_WIDTH,
    [columns]
  );

  const commitWidth = useCallback(
    (field, width) => {
      setWidths((prev) => {
        const next = { ...prev, [field]: width };
        writeWidths(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const resetWidth = useCallback(
    (field) => {
      setWidths((prev) => {
        if (!(field in prev)) return prev;
        const { [field]: _removed, ...next } = prev;
        writeWidths(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const stopDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    cancelAnimationFrame(drag.frame);
    window.removeEventListener("mousemove", drag.onMove);
    window.removeEventListener("mouseup", drag.onUp);
    document.body.style.cursor = drag.prevCursor;
    document.body.style.userSelect = drag.prevUserSelect;
    return drag;
  }, []);

  // Clean up listeners if the grid unmounts mid-drag.
  useEffect(() => stopDrag, [stopDrag]);

  const onMouseDownCapture = useCallback(
    (event) => {
      if (event.button !== 0) return;
      const hit = getFieldFromSeparator(event.target);
      if (!hit) return;

      event.preventDefault();
      event.stopPropagation();

      const { field, header } = hit;
      const minWidth = minWidthOf(field);
      const drag = {
        field,
        startX: event.clientX,
        startWidth: header.getBoundingClientRect().width,
        width: header.getBoundingClientRect().width,
        frame: 0,
        prevCursor: document.body.style.cursor,
        prevUserSelect: document.body.style.userSelect,
      };

      drag.onMove = (moveEvent) => {
        drag.width = Math.max(
          minWidth,
          Math.round(drag.startWidth + moveEvent.clientX - drag.startX)
        );
        cancelAnimationFrame(drag.frame);
        drag.frame = requestAnimationFrame(() => {
          if (apiRef.current?.setColumnWidth) {
            apiRef.current.setColumnWidth(drag.field, drag.width);
          } else {
            commitWidth(drag.field, drag.width);
          }
        });
      };

      drag.onUp = () => {
        const finished = stopDrag();
        if (finished && finished.width !== finished.startWidth) {
          // Swallow only the click that the browser fires right after this
          // mouseup (it would otherwise sort the column under the cursor).
          justDraggedRef.current = true;
          setTimeout(() => {
            justDraggedRef.current = false;
          }, 0);
          commitWidth(finished.field, finished.width);
        }
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", drag.onMove);
      window.addEventListener("mouseup", drag.onUp);
      dragRef.current = drag;
    },
    [apiRef, commitWidth, minWidthOf, stopDrag]
  );

  // The separator lives inside the header cell, so a click on it would
  // otherwise bubble up and toggle sorting.
  const onClickCapture = useCallback((event) => {
    if (justDraggedRef.current || getFieldFromSeparator(event.target)) {
      justDraggedRef.current = false;
      event.stopPropagation();
    }
  }, []);

  const onDoubleClickCapture = useCallback(
    (event) => {
      const hit = getFieldFromSeparator(event.target);
      if (!hit) return;
      event.stopPropagation();
      resetWidth(hit.field);
    },
    [resetWidth]
  );

  // Prop widths override the grid's internal state whenever `columns` changes
  // identity (many pages rebuild it every render on live ticks), so an
  // in-progress drag width must be reflected here too or the column snaps back.
  const resizedColumns = useMemo(
    () =>
      (columns || []).map((col) => {
        const drag = dragRef.current;
        const width =
          drag && drag.field === col.field ? drag.width : widths[col.field];
        if (width == null) return col;
        // A user-set width must win over flex sizing.
        const { flex: _flex, ...rest } = col;
        return { ...rest, width };
      }),
    [columns, widths]
  );

  const containerProps = useMemo(
    () => ({
      className: "resizable-columns-grid",
      onMouseDownCapture,
      onClickCapture,
      onDoubleClickCapture,
    }),
    [onMouseDownCapture, onClickCapture, onDoubleClickCapture]
  );

  return {
    columns: resizedColumns,
    apiRef,
    containerProps,
    resetAllWidths: useCallback(() => {
      writeWidths(storageKey, {});
      setWidths({});
    }, [storageKey]),
  };
};

export default useResizableColumns;
